import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AccumulationSeriesModel,
  AxisModel,
  Chart,
  ChartAnnotationSettingsModel,
  ChartAreaModel,
  FontModel,
  IAxisLabelRenderEventArgs,
  ILegendClickEventArgs,
  ILoadedEventArgs,
  IMouseEventArgs,
  IPointRenderEventArgs,
  ITooltipRenderEventArgs,
  LegendSettingsModel,
  MarginModel,
  SeriesModel,
  TooltipSettingsModel,
} from '@syncfusion/ej2-angular-charts';
import { LogService } from 'src/app/services/log.service';
import { UtilsService } from 'src/app/services/utils.service';
import * as lodash from 'lodash';

export interface ITapChartConfig {
  chartType?: 'Pie' | 'Linear';
  chartSeries?: SeriesModel[] | AccumulationSeriesModel[];
  useGroupingSeparator?: boolean;
  pallete?: string[];
  primaryXAxis?: AxisModel;
  primaryYAxis?: AxisModel;
  chartArea?: ChartAreaModel;
  tooltip?: boolean;
  tooltipSettings?: TooltipSettingsModel;
  legendSettings?: LegendSettingsModel;
  chartTheme?: string;
  chartHeight?: string;
  chartWidth?: string;
  chartTitle?: string;
  chartSubTitle?: string;
  backgroundColor?: string;
  titleStyle?: FontModel;
  subTitleStyle?: FontModel;
  seriesPalette?: string[];
  margin?: MarginModel;
  axes?: AxisModel[];
  annotations?: ChartAnnotationSettingsModel[];
  onPointClickCallback?: (args: any) => void;
  selectionMode?: 'Point' | 'Series' | 'Cluster' | 'DragXY' | 'DragX' | 'DragY' | 'Lasso' | 'None';
  displayLegend?:boolean;
  disableLegendToggle?: boolean;
}

@Component({
  selector: 'tap-chart',
  templateUrl: './tap-chart.component.html',
  styles: [
    `
      .no-data {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        /* Custom property, not a hardcoded value: an ancestor that knows its
           own no-data state stays this compact instead of reserving the same
           room as a populated chart (see tank-daily-usage-chart). Every other
           caller is unaffected - the fallback keeps today's height. */
        min-height: var(--tl-chart-empty-min-height, 160px);
      }

      .no-data .no-data-content {
      }

      .no-data .no-data-content .no-data-text {
        font-size: 1.3rem;
        font-weight: 700;
      }

      ejs-chart {
        overflow: visible !important;
      }
    `,
  ],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TapChartComponent implements OnInit {
  @Input() chartId: string = 'chart1';
  @Output() chartLoaded = new EventEmitter();
  @Output() axisLabelRender: EventEmitter<IAxisLabelRenderEventArgs> =
    new EventEmitter<IAxisLabelRenderEventArgs>();
  @Output() pointRender: EventEmitter<IPointRenderEventArgs> =
    new EventEmitter<IPointRenderEventArgs>();

  @Output() tooltipRender: EventEmitter<ITooltipRenderEventArgs> =
    new EventEmitter<ITooltipRenderEventArgs>();

  //two-way binding for selectedDataIndexes
  @Input() public selectedDataIndexes: any[] = [];

  private readonly dottedDashArray = '1, 2';
  private readonly chartTheme = 'Material';
  private chartApi?: Chart;
  private backgroundColor = '#ffffff00';
  private seriesFillColor =
    this.utilsService.getSiteWideThemeBackgroundColorHEXValue();

  public fontFamily = this.utilsService.getSiteWideThemeFontFamily();

  public labelStyle = {
    fontFamily: this.fontFamily,
    fontWeight: 'Bold',
  };

  public _chartConfig: ITapChartConfig = {
    axes: [],
    annotations: [],
    chartSeries: [],
    primaryXAxis: {
      majorGridLines: {
        dashArray: this.dottedDashArray,
      },
      labelStyle: this.labelStyle,
    },
    primaryYAxis: {
      majorGridLines: {
        dashArray: this.dottedDashArray,
      },
      labelStyle: this.labelStyle,
    },
    titleStyle: {
      fontFamily: this.fontFamily,
      fontWeight: 'Bolder',
      size: '18',
    },
    subTitleStyle: {
      fontFamily: this.fontFamily,
      fontWeight: 'Bolder',
      opacity: 0.5,
    },
    chartArea: {
      border: {
        width: 0,
      },
      background: this.backgroundColor,
    },
    tooltip: true,
    legendSettings: {
      visible: false,
      position: 'Bottom',
      textStyle: this.labelStyle,
    },
    chartTheme: this.chartTheme,
    chartHeight: '350',
    backgroundColor: this.backgroundColor,
  };
  public get chartConfig() {
    return this._chartConfig;
  }
  @Input()
  public set chartConfig(value: ITapChartConfig) {

    //inject  common series configuration into series config
    if (
      value?.chartType !== 'Pie' &&
      value?.chartSeries &&
      value?.chartSeries.length > 0
    ) {
      let chartConfigs = [];
      value.chartSeries.forEach((ct, idx) => {
        chartConfigs.push(
          lodash.merge(lodash.cloneDeep(this.commonSeriesConfig), ct),
        );
      });
      value.chartSeries = chartConfigs;
    }

    this._chartConfig = lodash.merge({}, this._chartConfig, value);

    // Ensure chartSeries array is completely replaced, not merged,
    // to prevent stale series data from persisting
    if (value?.chartSeries) {
      this._chartConfig.chartSeries = value.chartSeries;
    }

    if (this._chartConfig.chartTheme.toLowerCase().includes('dark')) {
      this._chartConfig.backgroundColor =
        this.utilsService.getSiteWideThemeDarkColorHEXValue();
      this._chartConfig.chartArea.background =
        this.utilsService.getSiteWideThemeDarkColorHEXValue();
    }

    if(this._chartConfig?.onPointClickCallback) {
      this._chartConfig.selectionMode = 'Point';
    }

    if (this.chartApi) {
      this.refreshChartInternal();
    }
  }

  public refreshingChart = false;
  public _chartData = [];
  public get chartData() {
    return this._chartData;
  }
  @Input() public set chartData(value) {
    const data = value ?? [];

    this._chartData.splice(0); // clear old data

    if (data.length === 0) {
      this.refreshingChart = false;
      if (this.chartApi) {
        this.chartApi.refresh();
      }
      return;
    }

    if (this.chartApi) {
      // Chart is already initialized — update data in-place and refresh without
      // destroying the DOM element, which would cause a layout reflow.
      data.forEach((d) => { this._chartData.push(d); });
      this.refreshChartInternal();
      return;
    }

    // First load: chart not yet initialized, use the standard recreate flow.
    this.refreshingChart = true;
    setTimeout(() => {
      try {
        this.refreshingChart = false;
        data.forEach((d) => {
          this._chartData.push(d);
        });
        this.cd.detectChanges();
      } catch (error) {
        this.logger.log('Chart data not available yet.', error, data);
      }
    }, 50);
  }

  private commonSeriesConfig: SeriesModel = {
    animation: {
      enable: false, //disable animation on series selection
      duration: 250, //milliseconds of animation
      delay: 250,
    },
  };

  /* String Resource | Value Binding */
  constructor(
    public utilsService: UtilsService,
    protected logger: LogService,
    protected cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {}

  //Setup additional chart features.
  public chartLoad(args: ILoadedEventArgs): void {
    // chartLoad event can happen more than once (refreshes or updates to chart config trigger this)
    if (this.chartApi) return;

    this.chartApi = args.chart;

    // setup tooltip options. This is left to typescript code since we have two tooltip directives.
    // one is for Syncfusion charting to define configuration and the other is to setup a tooltip pop-up material themed
    // component
    this.setupChartTooltip();

    if ((this._chartData?.length ?? 0) > 0 || (this._chartConfig?.chartSeries?.length ?? 0) > 0) {
      setTimeout(() => {
        this.refreshChartInternal();
      }, 0);
    }
  }

  //public pieChartLoad(args: ILoadedEventArgs): void {
  //todo:
  //this.chartApi = args.chart;
  //this.setupChartTooltip();
  //}

  public onChartLoaded(args: any): void {
    this.chartLoaded.emit({ context: this, args });
  }

  public onChartClick(args: IMouseEventArgs) {
    //do nothing, stub code.
  }

  public onLegendClick(arg: ILegendClickEventArgs) {

    if (this._chartConfig?.disableLegendToggle) {
      arg.cancel = true;
      return; // 🔥 VERY IMPORTANT (prevents your custom logic)
    }

     const seriesIndex = arg.series.index;
     const series = this.chartApi.series[seriesIndex];
     let status = true;
     let secYaxis = !series.visible;
     const isOtherSeriesVisible = this.chartApi.series[1 - seriesIndex].visible;
     const isOtherSeriesEmpty = this.checkIfSeriesHasData(1 - seriesIndex);
    if (series) {
      this.chartApi.series[seriesIndex].visible = !series.visible;
      series.visible = !series.visible;
     if(seriesIndex == 0){
        status = !series.visible
        if(!status){
          status = this.chartApi.series[1].visible;
        }

	    this.updatePrimaryYAxis(!series.visible, 1);

       this.updateSecondaryYAxisOpacity(
      (isOtherSeriesEmpty == false && secYaxis == false) ? 0 : 1
    );
      }
      else{
        status = !series.visible;
        if(!status){
          status = this.chartApi.series[0].visible;
        }
        this.updatePrimaryYAxisOpacity(
      (isOtherSeriesEmpty == false && secYaxis == false) ? 0 : 1
    );

    this.updateSecondaryYAxisOpacity((isOtherSeriesEmpty == false && secYaxis == false) ? 0 : 1
     ); // Keep it visible always if Delivery toggled

      }
    }

    this.chartApi.primaryXAxis = {
      ...this.chartApi.primaryXAxis,
      minimum: null, // Reset minimum value
      maximum: null, // Reset maximum value
      labelFormat: 'MM/dd/yy', // or other date format you prefer
      visible: status, // Set the visibility dynamically based on series visibility
  };
      this.chartApi.refresh();
      this.cd.detectChanges();

    this.logger.trackEvent(this._chartConfig.chartTitle, 'legendClick');
  }

// Function to check if a series has data
private checkIfSeriesHasData(seriesIndex: number): boolean {
  const seriesData = this._chartData.map(data => data[this.chartApi.series[seriesIndex].yName]);

  // Check if any value is valid (not null, undefined, NaN, or 0)
  return seriesData.some(value => value != null && value !== 0 && !Number.isNaN(value));
}

private updatePrimaryYAxis(visible: boolean, opacity: number): void {
  this.chartApi.primaryYAxis = {
    ...this.chartApi.primaryYAxis,
    visible,
    labelStyle: {
      ...this.chartApi.primaryYAxis.labelStyle,
      opacity: 1,
    },
  };
}

private updatePrimaryYAxisOpacity(opacity: number): void {
  this.chartApi.primaryYAxis = {
    ...this.chartApi.primaryYAxis,
    labelStyle: {
      ...this.chartApi.primaryYAxis.labelStyle,
      opacity,
    },
  };
}

private updateSecondaryYAxisOpacity(opacity: number): void {
  const secondaryAxis = this.chartApi.axes?.[0];
  if (!secondaryAxis) return;

  secondaryAxis.labelStyle = {
    ...secondaryAxis.labelStyle,
    opacity,
  };
}

  public onAxisLabelRender(arg: IAxisLabelRenderEventArgs) {
    //do nothing, stub code.
    this.axisLabelRender.emit(arg);
  }

  public onPointRender(arg: IPointRenderEventArgs) {
    this.pointRender.emit(arg);
  }

  public onTooltipRender(args: ITooltipRenderEventArgs) {
    this.tooltipRender.emit(args);
  }

  public refreshChart(): void {
    this.refreshChartInternal();
  }

  private refreshChartInternal(): void {
    if (!this.chartApi) {
      return;
    }

    // refreshBound() only exists on the Chart (cartesian) API, not on
    // AccumulationChart (Pie), which shares this same load/refresh handler.
    if (typeof (this.chartApi as Chart).refreshBound === 'function') {
      (this.chartApi as Chart).refreshBound();
    }

    this.chartApi.refresh();

    this.cd.detectChanges();
  }

  chartHasNoData(chartData: any[] | null): boolean {
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
      return true;
    }
    else{
      return false;
    }

    // Check if every data point in the array does not have meaningful data
    return chartData.every(d =>
      d.value <= 0 &&
      (d.above === undefined || d.above <= 0) &&
      (d.below === undefined || d.below <= 0) &&
      (d.met === undefined || d.met <= 0) &&
      !d.volume &&
      !d.averageDeliveryEfficiency
    );
  }

  private setupChartTooltip() {
    if (this.chartConfig.tooltipSettings) {
      this.chartApi.tooltip = this.chartConfig.tooltipSettings;
      if (this.chartConfig.tooltip != null) {
        this.chartApi.tooltip.enable = this.chartConfig.tooltip;
      }
    } else {
      this.chartApi.tooltip.enable = this.chartConfig.tooltip ?? true;
      this.chartApi.tooltip.textStyle = this.labelStyle;
    }
  }

  onPointClick(args: any) {
    this.logger.log('onPointClick', args);
    if (this._chartConfig.onPointClickCallback) {
      this._chartConfig.onPointClickCallback(args);
    }
  }

  chartMouseMove(args: any) {
    //this.logger.log("mousemove " + args?.target, args)
    let targetElement = document.getElementById(args?.target);
    if (targetElement) {
      targetElement.style.cursor = args.target.includes('Series')
        ? 'pointer'
        : 'default';
    }
  }
}
